// Batches frontend-observed events (PageView, LookupStarted, …) and reports them to
// POST /api/telemetry/events — see NetRiskScan.Api.Controllers.TelemetryController. The backend can't
// see SPA navigation or UI interactions on its own (the site is statically hosted, not served by the
// Api process), so this is the only source of that data.
//
// PageView is wired once at the public router root. Interaction events can call `trackEvent(...)`
// from their individual flows as they are instrumented.

import { getAttributionSnapshot, type AttributionSnapshot } from "./trafficAttribution";

export type TelemetryEventType =
  | "PageView"
  | "LookupStarted"
  | "LookupSucceeded"
  | "LookupFailed"
  | "CopyResult"
  | "ShareResult"
  | "ExportResult"
  | "LocaleChanged"
  | "ExternalLinkClicked"
  | "EnvironmentCheckStarted"
  | "EnvironmentCheckCompleted"
  | "ReportShared"
  | "NetworkCompared"
  | "SpeedTestStarted"
  | "SpeedTestCompleted"
  // Developer activation funnel UX telemetry (CRO pass) — these explain WHY a developer did or didn't
  // reach the next canonical stage; they are never themselves a canonical funnel stage. The canonical
  // Developer Funnel is recorded server-side only, via DeveloperFunnelEvent — see
  // NetRiskScan.Api.Controllers.TelemetryController's doc comment.
  | "DeveloperCtaClicked"
  | "PlanCtaClicked"
  | "RegistrationStarted"
  | "RegistrationSucceeded"
  | "VerificationResent"
  | "ApiKeyCreateStarted"
  | "QuickstartCopied"
  | "DeveloperDocsOpened";

export interface TelemetryEvent {
  eventType: TelemetryEventType;
  pagePath: string;
  locale?: string;
  durationMs?: number;
  /** The CURRENT page's referrer — document.referrer for the first view of a document, the previous
   * in-site page for a client-side route change. The server reduces it to just its host and shows it as
   * the row's "current referrer". It never decides the traffic source: for an in-site click it is this
   * very site. */
  referer?: string;
  /** How the visitor's session began (landing URL with its UTM tags, first referrer, session id).
   * Attached by trackEvent — callers never set it — so every event of a session carries the same
   * first-touch context and the server can attribute all of them to the session's original channel. */
  attribution?: AttributionSnapshot;
}

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/+$/, "");
const ENDPOINT = `${BASE_URL}/api/telemetry/events`;
const MAX_BATCH_SIZE = 10;
const FLUSH_INTERVAL_MS = 5000;

let buffer: TelemetryEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

export function trackEvent(event: TelemetryEvent): void {
  buffer.push({ ...event, attribution: getAttributionSnapshot() });

  if (buffer.length >= MAX_BATCH_SIZE) {
    flush();
    return;
  }

  if (flushTimer === null) {
    flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
  }
}

function flush(useBeacon = false): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  if (buffer.length === 0) return;

  const events = buffer;
  buffer = [];
  const payload = JSON.stringify({ events });

  // sendBeacon is the only delivery method reliable enough to survive page unload; a regular fetch
  // (even with keepalive) can be cancelled by the browser once the page is gone.
  if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
    const blob = new Blob([payload], { type: "application/json" });
    if (navigator.sendBeacon(ENDPOINT, blob)) {
      return;
    }
  }

  void fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    keepalive: true,
  }).catch(() => {
    // Best-effort only — a dropped telemetry batch must never surface to the user.
  });
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flush(true);
    }
  });
  window.addEventListener("pagehide", () => flush(true));
}
